import { animated, useSpring } from '@react-spring/web';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayS, LabelL } from '@sorare/core/src/atoms/typography';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  largeDesktopAndAbove,
  mediumMobileHeightAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import footballCard from './assets/footballCard.webp';
import mlbCard from './assets/mlbCard.webp';
import nbaCard from './assets/nbaCard.webp';

const mainMessage = defineMessages({
  title: {
    defaultMessage: 'Select your Card',
  },
  subtitle: {
    defaultMessage: 'Choose the Card you want to seal and click the seal icon.',
  },
});

const cardImageBySport = {
  [Sport.BASEBALL]: mlbCard,
  [Sport.NBA]: nbaCard,
  [Sport.FOOTBALL]: footballCard,
};

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  flex: 1;
  isolation: isolate;
  justify-content: space-between;

  & > * {
    z-index: 1;
    position: relative;
  }
`;

const Content = styled(Vertical)`
  width: 100%;
  flex: 1;
`;

const Padded = styled(Horizontal)`
  padding: 0 var(--double-unit);
  flex: 1;
  display: flex;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: auto;
  padding: var(--double-unit);
  display: flex;
  gap: var(--unit);
`;

const StyledButton = styled(Button)`
  flex: 1;
`;

const TextContent = styled(Vertical)`
  display: flex;
  align-items: center;
  position: relative;

  @media ${mediumMobileHeightAndAbove} {
    margin-top: var(--triple-unit);
  }

  & > * {
    padding-inline: var(--double-unit);
  }
`;

const Img = styled(animated.img)`
  margin: 0 auto;
  max-width: 240px;
  pointer-events: none;
  user-select: none;

  @media ${mediumMobileHeightAndAbove} {
    max-width: calc(100% - var(--quadruple-unit) * 2);
  }

  @media ${tabletAndAbove} {
    max-width: calc(100% - var(--quadruple-unit) * 5);
  }

  @media ${largeDesktopAndAbove} {
    max-width: calc(100% - var(--quadruple-unit) * 2);
  }
`;

const SpaceAroundVertical = styled(Vertical)`
  flex: 1;
  justify-content: space-around;
  img {
    max-width: 240px;
  }
`;

type Props = {
  nextStep: () => void;
};

export const SelectYourCard = ({ nextStep }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const [direction, setDirection] = useState<'next' | 'back'>('next');

  const transitions = useSpring({
    opacity: 1,
    transform: 'translateX(0%)',
    from: {
      opacity: 0,
      transform:
        direction === 'next' ? 'translateX(100%)' : 'translateX(-100%)',
    },
    config: { tension: 200, friction: 20 },
  });

  const imageSpring = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    config: { tension: 200, friction: 20 },
  });
  if (!currentUser) return null;

  return (
    <Wrapper>
      <Content>
        <TextContent gap={0.5}>
          <animated.div
            style={{ ...transitions, position: 'absolute', width: '100%' }}
          >
            <DisplayS className="text-center">
              <FormattedMessage
                {...mainMessage.title}
                values={{ nickname: currentUser?.nickname }}
              />
            </DisplayS>
          </animated.div>
        </TextContent>
        <SpaceAroundVertical gap={4}>
          <Img
            style={imageSpring}
            src={cardImageBySport[sport]}
            alt=""
            className="boxImage"
          />
        </SpaceAroundVertical>
        <div>
          <Padded center>
            <Box>
              <LabelL color="var(--c-nd-600)" className="text-center">
                <FormattedMessage {...mainMessage.subtitle} />
              </LabelL>
            </Box>
          </Padded>
        </div>
      </Content>
      <ButtonWrapper>
        <StyledButton
          fullWidth
          size="large"
          color="secondary"
          disableDebounce
          onClick={() => {
            setDirection('next');
            nextStep();
          }}
        >
          <FormattedMessage {...glossary.next} />
        </StyledButton>
      </ButtonWrapper>
    </Wrapper>
  );
};
